import { useState, useRef, useEffect, useCallback } from 'react'
import { motion, useInView } from 'framer-motion'

const stages = [
  {
    num: '01', label: 'Assess',
    title: 'Understanding Your Mandate',
    description: "We begin by mapping your global operating model — where the centre fits, what capabilities you need to own, and which leadership profile will define its first chapter. We challenge assumptions before we search.",
  },
  {
    num: '02', label: 'Establish',
    title: 'The Founding Leader',
    description: 'The founding hire shapes everything. We identify, assess, and place the Site Head, VP of Engineering, or CTO who will set the culture, attract the next 50 people, and navigate the parent organisation.',
  },
  {
    num: '03', label: 'Build',
    title: 'Core Capability Teams',
    description: 'With the founding leader in place, we systematically build the engineering, data, and product teams that give the centre its technical identity and give the business its competitive advantage.',
  },
  {
    num: '04', label: 'Scale',
    title: 'Strategic Expansion',
    description: 'As the centre matures, we expand across functions — from platform engineering to AI and ML, from product management to architect-level individual contributors who anchor long-term technical direction.',
  },
  {
    num: '05', label: 'Strengthen',
    title: 'Operational Excellence',
    description: 'We benchmark compensation, succession-plan senior roles, and support the centre in building the employer brand that makes world-class talent choose you over every other GCC in the city.',
  },
]

function StagePanel({ stage, index, onInView }) {
  const ref = useRef(null)
  const inView = useInView(ref, { amount: 0.55 })

  useEffect(() => { if (inView) onInView(index) }, [inView, index, onInView])

  return (
    <div
      ref={ref}
      className="py-16 md:py-20 border-t border-[#E5E7EB] first:border-t-0"
      data-testid={`gcc-stage-${index + 1}`}
    >
      <motion.div
        initial={{ opacity: 0, x: -16 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.75, ease: [0.16, 1, 0.3, 1] }}
      >
        <p className="text-[9px] tracking-[0.35em] uppercase text-[#0A0A0A]/25 mb-5 font-body font-semibold">
          {stage.num} / 05 &nbsp;&mdash;&nbsp; {stage.label}
        </p>
        <h3 className="text-2xl md:text-3xl lg:text-4xl font-heading font-black tracking-tight text-[#0A0A0A] mb-5 leading-tight">
          {stage.title}
        </h3>
        <p className="text-sm md:text-base font-body text-[#0A0A0A]/55 leading-relaxed max-w-xl">
          {stage.description}
        </p>
      </motion.div>
    </div>
  )
}

export default function GCCJourneySection() {
  const [activeStage, setActiveStage] = useState(0)
  const handleInView = useCallback((i) => setActiveStage(i), [])

  return (
    <section
      data-testid="gcc-journey-section"
      id="gcc-journey"
      className="bg-[#F6F7F9] py-32 md:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mb-20"
        >
          <p className="text-[10px] tracking-[0.35em] uppercase text-[#0A0A0A]/30 mb-5 font-body font-semibold">
            The GCC Journey
          </p>
          <h2 className="text-[clamp(2.5rem,5vw,5rem)] font-heading font-black tracking-tighter leading-none text-[#0A0A0A]">
            From concept to{' '}
            <span className="font-light text-[#16365F]">operating centre</span>
          </h2>
        </motion.div>

        <div className="flex gap-20 lg:gap-28">
          {/* Left: sticky navigator */}
          <div className="hidden lg:block w-48 flex-shrink-0">
            <div className="sticky top-32">
              {stages.map((stage, i) => (
                <div key={stage.num} className="py-4 border-b border-[#E5E7EB] last:border-0">
                  <p className={`text-xs font-body font-semibold transition-colors duration-400 ${
                    i === activeStage ? 'text-[#0B1F3A]' : 'text-[#0A0A0A]/22'
                  }`}>
                    <span className="mr-2 text-[#0A0A0A]/20">{stage.num}</span>
                    {stage.label}
                  </p>
                  <motion.div
                    className="h-px bg-[#0B1F3A] mt-2.5"
                    animate={{ scaleX: i === activeStage ? 1 : 0 }}
                    style={{ originX: 0 }}
                    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Right: content panels */}
          <div className="flex-1">
            {stages.map((stage, i) => (
              <StagePanel key={stage.num} stage={stage} index={i} onInView={handleInView} />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
